import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Typography, Grid, Card, CardContent, CardActionArea, Box,
  Chip, Skeleton, alpha, useTheme,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import CategoryIcon from '@mui/icons-material/Category';
import { listCategories, listArticles } from '../../utils/api';
import { Article, Category } from '../../utils/types';

const Home: React.FC = () => {
  const theme = useTheme();
  const [categories, setCategories] = useState<Category[]>([]);
  const [recentArticles, setRecentArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [cats, articles] = await Promise.all([listCategories(), listArticles()]);
        setCategories(cats.sort((a, b) => a.sortOrder - b.sortOrder));
        setRecentArticles(articles.filter(a => a.status === 'published').slice(0, 6));
      } catch (err) {
        console.error('Failed to load home data', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={60} />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>
          How can we help you?
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Browse our help articles, guides, and tutorials
        </Typography>
      </Box>

      {categories.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CategoryIcon color="primary" /> Categories
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {categories.map((cat) => (
              <Grid item xs={12} sm={6} md={3} key={cat.id}>
                <Card>
                  <CardActionArea component={RouterLink} to={`/category/${cat.slug}`}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {cat.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {cat.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {recentArticles.length > 0 && (
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArticleIcon color="primary" /> Recent Articles
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {recentArticles.map((article) => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <Card sx={{ height: '100%' }}>
                  <CardActionArea component={RouterLink} to={`/article/${article.slug}`} sx={{ height: '100%' }}>
                    <CardContent>
                      <Chip
                        label={article.category}
                        size="small"
                        sx={{
                          mb: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      />
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {article.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {article.excerpt}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default Home;
